import FilterValueRendererRouter from "../FilterValueRendererRouter";
import { ValueDisplayType, type FilterDefinition } from "../config/structured-search-schema";
import { resolveValuePresentation } from "../config/filter-value-type-behavior";

import MultiSelectCheck from "./MultiSelectCheck";

import { cn } from "@/lib/utils";

const COUNT_UNITS = [
  { value: 1_000_000_000_000, suffix: "t" },
  { value: 1_000_000_000, suffix: "b" },
  { value: 1_000_000, suffix: "m" },
  { value: 1_000, suffix: "k" },
] as const;

const formatSuggestionCount = (count: number): string => {
  for (const unit of COUNT_UNITS) {
    if (count >= unit.value) {
      const rounded = Math.round((count / unit.value) * 10) / 10;
      return `${rounded.toFixed(1).replace(/\.0$/, "")}${unit.suffix}`;
    }
  }

  return count.toString();
};

interface ValueOptionRowProps {
  value: string;
  renderedValue: string;
  count?: number;
  highlighted: boolean;
  disabled?: boolean;
  definition?: FilterDefinition;
  isMulti: boolean;
  isSelected: boolean;
  onSelect?: () => void;
}

const ValueOptionRow = ({
  value,
  renderedValue,
  count,
  highlighted,
  disabled,
  definition,
  isMulti,
  isSelected,
  onSelect,
}: ValueOptionRowProps) => {
  const formattedCount = count !== undefined ? formatSuggestionCount(count) : undefined;
  const presentation = resolveValuePresentation(definition);
  const usesSpecialPresentation = presentation !== ValueDisplayType.Default;

  const content = (
    <>
      <FilterValueRendererRouter
        definition={definition}
        value={value}
        label={renderedValue}
        textClassName="truncate"
      />
      {formattedCount !== undefined && (
        <span className="ml-2 shrink-0 text-slate-400">({formattedCount})</span>
      )}
    </>
  );

  return (
    <li
      key={value}
      role="option"
      aria-selected={highlighted}
      aria-disabled={disabled}
      className={cn(
        "mx-2 mb-1 flex h-[38px] items-center overflow-hidden rounded-xl px-3 text-sm transition-colors",
        disabled
          ? "cursor-not-allowed text-slate-400 opacity-70"
          : "cursor-pointer hover:bg-slate-50",
        highlighted && !disabled ? "bg-slate-100 text-slate-900" : undefined,
        !highlighted && !disabled ? "text-slate-700" : undefined,
        usesSpecialPresentation && "gap-0",
      )}
      onMouseDown={e => {
        e.preventDefault();
        if (!disabled) {
          onSelect?.();
        }
      }}
    >
      {isMulti && <MultiSelectCheck selected={isSelected} />}
      {content}
    </li>
  );
};

export default ValueOptionRow;
